import { stringify } from "querystring";

import { ChangeEvent, useRef, useState } from "react";
import { Button, useWindowSize } from "@newsela/angelou";
import { useRouter } from "next/router";

import {
  actionNames,
  actionPrefixes,
  eventTypes,
  objectTypes,
  useTracker,
  withTrackEvent,
} from "@newsela/snitch";

import AISearchWithSparkleIcon from "@/icons/AISearchWithSparkleIcon/AISearchWithSparkleIcon";

import { NAVBAR_BREAKPOINTS } from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/constants";
import { useNewSearchSessionTracker } from "@/utils/snitch";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema/LangstonParameterSchema";
import AIExpandableTextArea from "../AIExpandableTextArea";

import {
  buttonWithPopOutRootCss,
  buttonIconContainerCss,
  buttonIconBorderCss,
  searchToggleButtonSvgCss,
  popOutCss,
} from "./AISearchMenuContainer.styles";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface AISearchMenuContainerProps {
  onToggle: (value: boolean) => void;
  isAISearchEnabled: boolean;
}

const AISearchMenuContainer = ({
  onToggle,
  isAISearchEnabled,
}: AISearchMenuContainerProps) => {
  const { push, query, pathname } = useRouter();

  const [expanded, setExpanded] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { width: windowWidth } = useWindowSize();
  const isMobileDisplay = windowWidth < NAVBAR_BREAKPOINTS.sm;

  const { trackAction, getEventName } = useTracker();
  const { startSearchTracker } = useNewSearchSessionTracker({
    is_luna_search: true,
  });

  const handleContainerBlur = (e: React.FocusEvent) => {
    // Ensures that tabbing into elements within container
    // doesn't collapse the container. Only collapses when
    // focus is set outside of container.
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setExpanded(false);
    }
  };

  const handleExpandAndFocus = () => {
    setExpanded(true);

    trackAction({
      event_type: eventTypes.click,
      event_name: getEventName(
        objectTypes.user,
        actionPrefixes.initiate,
        actionNames.interact,
      ),
      dimension_properties: {
        description: "Toggle open the Search with Luna input.",
      },
    });
  };

  const handleToggleSearch = () => {
    setExpanded(!expanded);
  };

  const handleToggle = () => {
    const newValue = !isAISearchEnabled;
    if (pathname === "/search") {
      const searchParams = new URLSearchParams(stringify(query));
      searchParams.delete(LangstonParameterSchema.prompt.key);
      void push({
        pathname: "/search",
        search: `?${searchParams.toString()}`,
      });
    }
    onToggle(newValue);
  };

  const resetSearchMenuToDefault = () => {
    setInputValue("");
  };

  const onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = event?.target?.value;
    setInputValue(inputValue);

    if (!inputValue) {
      resetSearchMenuToDefault();
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Escape") {
      setExpanded(false);
    }

    if (event.key === "Enter" && !event.shiftKey) {
      // Prevents textarea's native newline functionality so it can
      // submit the query instead.
      event.preventDefault();

      startSearchTracker(inputValue);
      const searchParams = new URLSearchParams(stringify(query));
      searchParams.set(LangstonParameterSchema.prompt.key, inputValue);

      void push({
        pathname: "/search",
        search: `?${searchParams.toString()}`,
      });

      setInputValue("");
      // Collapse after submit
      setExpanded(false);
    }
  };

  return (
    <>
      {isMobileDisplay ? (
        <li>
          <ButtonWithTrackEvent
            flavor={Button.flavors.incognito}
            size={Button.sizes.sm}
            onClick={handleToggleSearch}
            onBlur={handleContainerBlur}
            __cssFor={{
              root: buttonWithPopOutRootCss,
            }}
            objectType={objectTypes.user}
            actionName={actionNames.interact}
            streamProperties={{
              description: "Toggle Search with Luna search bar on mobile.",
            }}
            dataProps={{
              "data-qa-selector": "search-with-luna-mobile-toggle-button",
            }}
          >
            <div css={buttonIconContainerCss}>
              <div css={buttonIconBorderCss}>
                <AISearchWithSparkleIcon css={searchToggleButtonSvgCss} />
              </div>
            </div>
          </ButtonWithTrackEvent>
          {expanded ? (
            <div css={popOutCss}>
              <AIExpandableTextArea
                containerRef={containerRef}
                expanded={expanded}
                handleContainerBlur={handleContainerBlur}
                handleExpandAndFocus={handleExpandAndFocus}
                inputValue={inputValue}
                isMobileDisplay
                textAreaRef={textAreaRef}
                onToggle={handleToggle}
                pressed={isAISearchEnabled}
                onChange={onChange}
                onKeyDown={onKeyDown}
              />
            </div>
          ) : null}
        </li>
      ) : (
        <li>
          <AIExpandableTextArea
            containerRef={containerRef}
            expanded={expanded}
            handleExpandAndFocus={handleExpandAndFocus}
            handleContainerBlur={handleContainerBlur}
            inputValue={inputValue}
            textAreaRef={textAreaRef}
            onToggle={handleToggle}
            pressed={isAISearchEnabled}
            onChange={onChange}
            onKeyDown={onKeyDown}
          />
        </li>
      )}
    </>
  );
};

export default AISearchMenuContainer;
